import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { EnvironmentLogo } from '../../models/environment-logo/environment-logo';
import { MenuItem, SubMenuItem } from '../../models/menu-item';
import { MenuService } from '../../services/menu.service';
import { NotificationService } from '../../services/notification.service';
import { Store } from '../../store';
import { ToolsService } from '../../services/tools.service';

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit, OnDestroy {
    domainClass = 'ys';

    @HostBinding('class') get class() {
        return this.domainClass;
    }

    public domain$ = this.store.select<string>('domain');
    // Menu Items Variables Start

    public myMenuItems$: BehaviorSubject<MenuItem[]> = new BehaviorSubject([]);
    public bottomMenuItems$: BehaviorSubject<MenuItem[]> = new BehaviorSubject([]);
    public user$ = this.store.select<any>('user');

    public user = this.store.selectForLocal('user');
    public hoveredMenuItem: MenuItem;

    // Menu Items Variables End

    // Environment List Variables Start

    public isEnvironmentListOpen = false;
    public environmentLogos: EnvironmentLogo[] = [
        {
            path: 'assets/images/upero.svg',
            name: 'upero-logo',
            domain: 'up',
        },
    ];

    public defaultEnvironmentLogo: EnvironmentLogo = {
        path: 'assets/images/black-logo.svg',
        name: 'yss-logo',
        domain: 'ys',
    };

    // Environment List Variables End

    private destroy$ = new Subject<void>();
    private navigationUrl = undefined;

    constructor(
        private store: Store,
        private router: Router,
        private menuService: MenuService
    ) {}

    public ngOnInit(): void {
        this.domain$.pipe(takeUntil(this.destroy$)).subscribe((val) => this.domainClass = val);
        const isSideBarOpen = localStorage.getItem('isSideBarOpen');
        if (isSideBarOpen === 'true') {
            this.store.set('isSideBarOpen', true);
        } else {
            this.store.set('isSideBarOpen', false);
        }

        this.store
            .select('user')
            .pipe(
                filter((user: any) => user),
                map(({ permissions }: any) => permissions),
                filter((permissions) => permissions),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((permissions: any) => {
                const myMenuItems = this.store.selectForLocal('menuItems').reduce((acc, menuItem) => {
                    if (menuItem.perm && permissions[menuItem.perm]) {
                        acc.push({
                            // only if it's allowed
                            ...menuItem,
                            subMenus: menuItem.subMenus.filter(
                                (subItem) => (subItem.perm && permissions[menuItem.perm][subItem.perm]) || !subItem.perm
                            ),
                        });
                    }
                    return acc;
                }, []);

                this.myMenuItems$.next(myMenuItems.filter((menuItem) => !menuItem.isBottomMenuItem));
                this.bottomMenuItems$.next(myMenuItems.filter((menuItem) => menuItem.isBottomMenuItem));
                this._checkAndSetActiveMenuItem();
            });

        this.router.events
            .pipe(
                filter((routerEvent) => routerEvent instanceof NavigationStart),
                takeUntil(this.destroy$)
            )
            .subscribe((navigationEndEvent: NavigationStart) => {
                this.navigationUrl = navigationEndEvent.url;
                this._setActiveMenuItem();
                this.hoveredMenuItem = null;
            });

        const activeDomain = localStorage.getItem('activeDomain');
        let currentEnvironmentLogo = this.environmentLogos.find(
            (environmentLogo) => environmentLogo.domain === activeDomain
        );

        if (!currentEnvironmentLogo) {
            currentEnvironmentLogo = this.defaultEnvironmentLogo;
        }
        this.setDefaultLogo(currentEnvironmentLogo, this.defaultEnvironmentLogo, false);
    }

    public ngOnDestroy(): void {
        this._defaultActiveMenuItems();
        this.destroy$.next();
    }

    public parentCollapsedAndChildActive(menuItem: MenuItem): boolean {
        return menuItem.subMenus.some((menuItem) => menuItem.active) || menuItem.active;
    }

    public toggleSidebar() {
        const newState = !this.isSidebarOpen;
        this.store.set('isSideBarOpen', newState);
        if (this.isSidebarOpen) {
            this.hoveredMenuItem = null;
            localStorage.setItem('isSideBarOpen', 'true');
        } else {
            localStorage.removeItem('isSideBarOpen');
            localStorage.setItem('isSideBarOpen', 'false');
        }
    }

    get isSidebarOpen(): boolean {
        return this.store.selectForLocal('isSideBarOpen');
    }

    public toggleCollapsedChildMenu(menuItem: MenuItem) {
        if (menuItem.subMenus.length && menuItem.route == '') {
            menuItem.toggleChildMenu = true;
            this.hoveredMenuItem = menuItem;
        } else {
            menuItem.toggleChildMenu = false;
            this.hoveredMenuItem = null;
        }
    }

    public toggleEnvironmentList(): void {
        this.isEnvironmentListOpen = this.isEnvironmentListOpen ? false : true;
    }

    public toggleMenuItems(item): void {
        item.collapsed = !item.collapsed;
        const menuItemsFromStore = this.store.selectForLocal('menuItems');
        const menuItemFromStore = menuItemsFromStore.find((m) => m.menuGroup === item.menuGroup);
        menuItemFromStore.collapsed = item.collapsed;
        this.store.set('menuItems', menuItemsFromStore);
    }

    public setDefaultLogo(
        environmentLogoToSet: EnvironmentLogo,
        currentDefaultEnviromentLogo: EnvironmentLogo,
        setDomain: boolean
    ): void {
        const environmentLogoToSetIndex = this.environmentLogos.findIndex(
            (environmentLogo) => environmentLogo === environmentLogoToSet
        );

        if (environmentLogoToSetIndex >= 0) {
            this.environmentLogos = this.environmentLogos.splice(environmentLogoToSetIndex, environmentLogoToSetIndex);

            this.environmentLogos.push(currentDefaultEnviromentLogo);

            this.defaultEnvironmentLogo = environmentLogoToSet;
        }

        if (setDomain) {
            this.menuService.setDomain(this.defaultEnvironmentLogo.domain);
        }

        this.isEnvironmentListOpen = false;
    }

    public getHoveredMenuPosition(hoveredMenuItemMenuGroup: string) {
        switch (hoveredMenuItemMenuGroup) {
            case 'actions':
                return 'actions';
            case 'orders':
                return 'orders';
            case 'accounts':
                return 'accounts';
            case 'cms':
                return 'cms';
            case 'cmsNew':
                return 'cms-new';
        }
    }

    private _setActiveMenuItem() {
        if (!this.navigationUrl) {
            return;
        }
        let url = this.navigationUrl;
        const originalUrl = url;
        const urlQueryParamIndex = url.indexOf('?');
        url = urlQueryParamIndex >= 0 ? url.slice(0, urlQueryParamIndex) : url;

        this.myMenuItems$.value.forEach((menuItem) => {
            ('/' + menuItem.route === url && menuItem.route) || (url === '/' && menuItem.route === 'dashboard')
                ? (menuItem.active = true)
                : (menuItem.active = false);
            menuItem.subMenus.map((subMenuItem) => {
                if (subMenuItem.id === 'addAction') {
                    subMenuItem.active = subMenuItem.route === url && !originalUrl.includes('quote');
                } else if (subMenuItem.id === 'addQuote') {
                    subMenuItem.active = originalUrl.includes('quote');
                } else {
                    subMenuItem.route === url
                        ? ((subMenuItem.active = true), (menuItem.collapsed = false))
                        : (subMenuItem.active = false);
                }
            });
        });

        this.bottomMenuItems$.next(
            this.bottomMenuItems$.value.map((menuItem) => {
                menuItem.active = menuItem.defaultRoute === url;
                menuItem.subMenus.map((subMenu: SubMenuItem) => {
                    subMenu.active = subMenu.route === url;
                    if (subMenu.active) {
                        menuItem.active = true; // there are menues that are not rootet from the same route
                    }

                    return subMenu;
                });

                return menuItem;
            })
        );
    }

    private _checkAndSetActiveMenuItem() {
        const menusWithSubMenuItems = this.myMenuItems$.value.filter((menuItem) => menuItem.subMenus.length);

        const subMenuActiveNotSet = menusWithSubMenuItems.every((menuItem) =>
            menuItem.subMenus.every((subMenuItem) => !subMenuItem.active)
        );

        if (subMenuActiveNotSet) {
            this._setActiveMenuItem();
        }
    }

    private _defaultActiveMenuItems() {
        this.myMenuItems$.value.map((menuItem) => {
            menuItem.collapsed = true;
            menuItem.subMenus.map((subMenuItem) => (subMenuItem.active = false));
            return menuItem;
        });
    }
}
